import { forwardRef, useEffect } from 'react';
import { MenuPlate } from '../MenuPlate';
import { NoResults } from '../NoResults';
import { Product } from '../../../../interfaces/domain/menu';
import { useMenuPlates } from '../../../../hooks/useMenuPlates';
import styles from './MenuPlates.module.scss';
import { useCategoriesStore, useOrdersStore, useUIStore } from '../../../../store';

type MenuPlatesProps = {
  menuData?: any;
};

export const MenuPlates = forwardRef<HTMLDivElement, MenuPlatesProps>(({ menuData }, ref) => {
  const { categoriesToDisplay, isEmpty } = useMenuPlates(menuData);

  const showSearch = useUIStore((s) => s.showSearch);
  const canUseCart = useOrdersStore((s) => s.canUseCart);

  const setCategorySelected = useCategoriesStore((s) => s.setCategorySelected);

  useEffect(() => {
    const sections = document.querySelectorAll("section[id^='cat-']");
    const categoriesBar = document.querySelector<HTMLElement>('[data-sticky-categories-bar]');

    const handleScroll = () => {
      const { isManualScrolling } = useCategoriesStore.getState();

      if (isManualScrolling) return;

      if (showSearch) {
        setCategorySelected(null);
        return;
      }

      const STICKY_OFFSET = categoriesBar
        ? categoriesBar.getBoundingClientRect().bottom
        : 128;

      if (window.scrollY < 50) {
        setCategorySelected(null);
        return;
      }

      let currentId = null;
      let currentName = "";

      const isAtBottom =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 10;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();

        if (rect.top <= STICKY_OFFSET) {
          currentId = section.id.replace("cat-", "");
          currentName = section.getAttribute("data-name") || "";
        }

        if (isAtBottom) {
          currentId = section.id.replace("cat-", "");
          currentName = section.getAttribute("data-name") || "";
        }
      });

      if (currentId) {
        setCategorySelected({
          id: currentId,
          name: currentName,
        });
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setCategorySelected, showSearch]);

  useEffect(() => {
    if (showSearch) {
      const timeout = setTimeout(() => {
        const container = document.querySelector(`.${styles.containerMenuPlatesFormSearch}`);

        if (container) {
          const STICKY_OFFSET = 120;
          const elementPosition = container.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: elementPosition - STICKY_OFFSET,
            behavior: 'smooth',
          });
        } else {
          const fallback = document.querySelector(`.${styles.container}`);
          if (fallback) {
            fallback.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 150);

      return () => clearTimeout(timeout);
    }
  }, [showSearch]);

  if (isEmpty) {
    return (
      <div className={styles.containerNoResults}>
        <NoResults />
      </div>
    );
  }

  return (
    <div
      className={`${showSearch
        ? styles.containerMenuPlatesFormSearch
        : canUseCart
          ? styles.containerMenuPlatesWithTotal
          : styles.containerMenuPlatesWithOutTotal
        }`}
      ref={ref}
    >
      {categoriesToDisplay.map((category) => (
        <section
          key={category.id}
          id={`cat-${category.id}`}
          data-name={category.name}
          className={styles.scrollTarget}
        >
          <h2 className={styles.nameCategory}>
            {category.name}
          </h2>

          {category.description && (
            <p className={styles.categoryDescription}>
              {category.description}
            </p>
          )}

          {!!category.products?.length && (
            <div className={styles.gridWrapper}>
              {category.products.map((plate: Product) => (
                <div key={plate.id} className={styles.plateBox}>
                  <MenuPlate plate={plate} />
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
});

MenuPlates.displayName = 'MenuPlates';